import React from 'react';
import { Case } from '../types';
import { getRoleDisplay, getRoleBadgeClass } from '../utils/roles';

interface CaseStatusBannerProps {
  case: Case;
}

export function CaseStatusBanner({ case: caseData }: CaseStatusBannerProps) {
  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (caseData.status === 'resolved' && caseData.resolvedAt) {
    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Περιστατικό Επιλύθηκε
            </h3>
            
            {/* Final Diagnosis */}
            {caseData.finalDiagnosis && (
              <div className="mb-3">
                <h4 className="font-medium text-blue-800 mb-1">Τελική Διάγνωση:</h4>
                <p className="text-blue-700 bg-white p-3 rounded border">
                  {caseData.finalDiagnosis}
                </p>
              </div>
            )}

            {/* Resolution Notes */}
            {caseData.resolutionNotes && (
              <div className="mb-3">
                <h4 className="font-medium text-blue-800 mb-1">Σημειώσεις Επίλυσης:</h4>
                <p className="text-blue-700 bg-white p-3 rounded border whitespace-pre-wrap">
                  {caseData.resolutionNotes}
                </p>
              </div>
            )}

            {/* Resolution Details */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-blue-600">
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Επιλύθηκε στις {formatDate(caseData.resolvedAt)}</span>
              </div>

              {caseData.resolvedByUser && (
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>από</span>
                  <span className="font-medium">
                    Δρ. {caseData.resolvedByUser.firstName} {caseData.resolvedByUser.lastName}
                  </span>
                  {caseData.resolvedByUser.role && (
                    <span className={getRoleBadgeClass(caseData.resolvedByUser.role)}>
                      {getRoleDisplay(caseData.resolvedByUser.role)}
                    </span>
                  )}
                  {caseData.resolvedByUser.specialty && (
                    <span className="text-xs text-blue-500">
                      ({caseData.resolvedByUser.specialty})
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (caseData.status === 'archived') {
    return (
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8l6 6M5 8l6-6m0 12l6-6m0 0l6 6m-6-6l6-6" />
              </svg>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Περιστατικό Αρχειοθετημένο
            </h3>
            <p className="text-sm text-gray-600">
              Αυτό το περιστατικό έχει αρχειοθετηθεί και είναι διαθέσιμο στο αρχείο για όλους τους χρήστες.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // For open cases, no banner needed
  return null;
}